import Plot from 'react-plotly.js'
import { Data, Layout } from 'plotly.js'

interface TimeSeriesData {
  x: string[] | Date[]
  y: number[]
  name: string
  color?: string
  type?: 'line' | 'scatter' | 'area'
}

interface TimeSeriesChartProps {
  data: TimeSeriesData[]
  title?: string
  xAxisTitle?: string
  yAxisTitle?: string
  height?: number
  showLegend?: boolean
  threshold?: number
  thresholdLabel?: string
  className?: string
}

export default function TimeSeriesChart({
  data,
  title,
  xAxisTitle,
  yAxisTitle,
  height = 300,
  showLegend = true,
  threshold,
  thresholdLabel = 'Threshold',
  className
}: TimeSeriesChartProps) {
  const traces: Data[] = data.map((series) => ({
    x: series.x,
    y: series.y,
    name: series.name,
    type: 'scatter',
    mode: series.type === 'scatter' ? 'markers' : 'lines',
    fill: series.type === 'area' ? 'tozeroy' : undefined,
    line: {
      color: series.color || '#3b82f6',
      width: 2
    },
    marker: {
      color: series.color || '#3b82f6',
      size: 6
    }
  }))

  // Add threshold line if specified - with comprehensive null safety
  if (threshold !== undefined && data.length > 0 && data[0]?.x?.length > 0) {
    traces.push({
      x: [data[0].x[0], data[0].x[data[0].x.length - 1]],
      y: [threshold, threshold],
      name: thresholdLabel,
      type: 'scatter',
      mode: 'lines',
      line: {
        color: '#ef4444',
        width: 2,
        dash: 'dash'
      }
    })
  }

  const layout: Partial<Layout> = {
    title: title ? {
      text: title,
      font: { color: '#e2e8f0', size: 14 }
    } : undefined,
    height,
    margin: { t: title ? 40 : 20, r: 20, b: 40, l: 50 },
    paper_bgcolor: 'transparent',
    plot_bgcolor: 'transparent',
    font: { color: '#94a3b8', family: 'Inter, sans-serif' },
    xaxis: {
      title: xAxisTitle ? { text: xAxisTitle, font: { color: '#94a3b8', size: 12 } } : undefined,
      gridcolor: '#334155',
      linecolor: '#475569',
      tickfont: { size: 10 },
      showgrid: true,
      zeroline: false
    },
    yaxis: {
      title: yAxisTitle ? { text: yAxisTitle, font: { color: '#94a3b8', size: 12 } } : undefined,
      gridcolor: '#334155',
      linecolor: '#475569',
      tickfont: { size: 10 },
      showgrid: true,
      zeroline: false
    },
    showlegend: showLegend,
    legend: {
      orientation: 'h',
      yanchor: 'bottom',
      y: 1.02,
      xanchor: 'right',
      x: 1,
      font: { size: 10 }
    },
    hovermode: 'x unified'
  }

  const config = {
    displayModeBar: false,
    responsive: true
  }

  return (
    <div className={className}>
      <Plot
        data={traces}
        layout={layout}
        config={config}
        style={{ width: '100%' }}
      />
    </div>
  )
}
